import React, { createContext, useContext } from "react";
import { Block } from "@babackapp/editor-blocks-library/blocks";
import { useFormContext } from "react-hook-form";
import { useBlockIndexSection } from "../hooks/use-block-index-section";

interface EditorSelectedBlockContextValue {
  selectedBlock: Block | null;
  setSelectedBlock: (block: Block | null) => void;
  hoveredBlock: Block | null;
  setHoveredBlock: (block: Block | null) => void;
}

const EditorSelectedBlockContext = createContext<
  EditorSelectedBlockContextValue | undefined
>(undefined);

export function EditorSelectedBlockProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const form = useFormContext();
  const [selectedBlock, setSelectedBlock] = React.useState<Block | null>(null);
  const [hoveredBlock, setHoveredBlock] = React.useState<Block | null>(null);
  const { section: selectedSection, index: selectedIndex } =
    useBlockIndexSection(selectedBlock?.id || "");
  const selectedBlockData = form.watch(
    `content.${selectedSection}Blocks.${selectedIndex}`,
  ) as Block;

  const { section: hoveredSection, index: hoveredIndex } = useBlockIndexSection(
    hoveredBlock?.id || "",
  );
  const hoveredBlockData = form.watch(
    `content.${hoveredSection}Blocks.${hoveredIndex}`,
  ) as Block;

	const value = React.useMemo(() => ({
		selectedBlock: selectedBlockData || selectedBlock,
		setSelectedBlock,
		hoveredBlock: hoveredBlockData || hoveredBlock,
		setHoveredBlock,
	}), [selectedBlockData, selectedBlock, setSelectedBlock, hoveredBlock, selectedBlockData]);

  return (
    <EditorSelectedBlockContext.Provider
      value={value}
    >
      {children}
    </EditorSelectedBlockContext.Provider>
  );
}

export const useEditorSelectedBlock = () => {
  const context = useContext(EditorSelectedBlockContext);

  if (context === undefined) {
    throw new Error(
      "useEditorSelectedBlock must be used within an EditorSelectedBlockProvider",
    );
  }
  return context;
};
